import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { shouldShowNeedsUpdating } from "Payments/Shared/shouldShowNeedsUpdating";
import classNames from "classnames";
import { motion } from "framer-motion";

import useResizeObserver from "Utilities/Hooks/useResizeObserver";

import Notice from "../../Shared/Notice";
import { useManagePaymentMethods } from "../ManagePaymentMethodsContext";

import CardBackgrounds from "./CardBackgrounds";
import {
  PaymentMethodProvider,
  usePaymentMethod,
} from "./PaymentMethodContext";
import QuickControls from "./QuickControls";
import SharedBy from "./SharedBy";
import { useIsUnusable, useNeedsUpdate } from "./paymentMethodStateHooks";

const widthBreakpoints = [360, 320];

PaymentMethod.propTypes = {
  showQuickControls: PropTypes.bool,
  hasMobilePhone: PropTypes.bool,
  onCardSelect: PropTypes.func,
  allPaymentMethods: PropTypes.array,
  isAnimatingIn: PropTypes.bool,
  networkName: PropTypes.string,
};

function PaymentMethod({
  showQuickControls,
  hasMobilePhone,
  onCardSelect,
  allPaymentMethods,
  isAnimatingIn = false,
  updateExternalPaymentMethods,
  networkName,
  ...initialPaymentData
}) {
  const { state, dispatch } = usePaymentMethod();
  const { dispatch: mpmDispatch } = useManagePaymentMethods();
  const { needsUpdate, message } = useNeedsUpdate();
  const { isUnusable } = useIsUnusable();
  const isUsable = !isUnusable;

  const {
    ID,
    paymentType,
    cardType,
    nickname,
    last4,
    expirationMonth,
    expirationYear,
    hasOtherOwner,
    owner = {},
  } = state;

  const showUpdateNotice = shouldShowNeedsUpdating(state);

  useEffect(() => {
    if (!initialPaymentData.mounted) {
      dispatch({
        type: "Set Payment Method",
        payload: {
          ...initialPaymentData,
          paymentMethodID: initialPaymentData.ID,
        },
      });
      mpmDispatch({ type: "Payment Method Mounted", payload: ID });
    }
  }, [ID, dispatch, mpmDispatch, initialPaymentData]);

  const cardRef = useRef();

  const [widthClassNames, setWidthClassNames] = useState(
    "card_type_" + cardType
  );

  useResizeObserver(cardRef, (e) => {
    const widthClasses = [];
    widthBreakpoints.forEach((breakpoint) => {
      if (e.borderBoxSize[0].inlineSize < breakpoint)
        widthClasses.push("width_" + breakpoint);
    });
    setWidthClassNames(classNames("card_type_" + cardType, widthClasses));
  });

  const handleCardClick = () => {
    isUsable && onCardSelect && onCardSelect(state);
  };

  const getNickname = () => {
    if (nickname) {
      return <Nickname>{nickname}</Nickname>;
    } else if (paymentType === "bank") {
      return (
        <Nickname>
          {cardType[0].toUpperCase() + cardType.slice(1)} Account
        </Nickname>
      );
    } else {
      return "";
    }
  };

  const getLowerRightContent = () => {
    if (paymentType === "card") {
      return (
        <>
          {expirationMonth} / {expirationYear?.substr(-2)}
        </>
      );
    } else if (paymentType === "bank" && nickname) {
      return cardType[0].toUpperCase() + cardType.slice(1);
    } else {
      return "";
    }
  };

  return (
    <Container
      layout
      variants={{
        hidden: { opacity: 0, y: 80, scale: 0.8 },
        show: { opacity: 1, y: 0, scale: 1 },
      }}
      exit={{
        x: 300,
        opacity: 0,
        scale: 0.8,
        rotate: 12,
        y: 40,
        transition: { type: "tween" },
      }}
      className={widthClassNames}
    >
      <CardAndControlsHolder>
        {showUpdateNotice && <UpdateNotice>{message}</UpdateNotice>}
        <Wrapper
          ref={cardRef}
          style={isUsable && onCardSelect && { cursor: "pointer" }}
          whileHover={isUsable && onCardSelect ? { scale: 1.04 } : {}}
          whileTap={isUsable && onCardSelect ? { scale: 0.98 } : {}}
          onClick={handleCardClick}
        >
          <CardBackgrounds cardType={cardType} />
          <CardContent>
            <CardInside>
              {hasOtherOwner && <SharedBy {...owner} />}
              {getNickname()}
              <CardInfo>
                <CardNumber>&bull; &bull; &bull; &bull; {last4}</CardNumber>
                <ExpirationDate>{getLowerRightContent()}</ExpirationDate>
              </CardInfo>
            </CardInside>
          </CardContent>
        </Wrapper>
        <QuickControls
          showQuickControls={showQuickControls}
          hasMultiplePaymentMethods={allPaymentMethods.length > 1}
          hasMobilePhone={hasMobilePhone}
          hasOtherOwner={hasOtherOwner}
          updateExternalPaymentMethods={updateExternalPaymentMethods}
          networkName={networkName}
        />
      </CardAndControlsHolder>
      <Padding />
    </Container>
  );
}

// EXPORTED COMPONENT WITH PROVIDER
export default function PaymentMethodWithProvider(props) {
  return (
    <PaymentMethodProvider>
      <PaymentMethod {...props} />
    </PaymentMethodProvider>
  );
}

const CardAndControlsHolder = styled.div`
  position: relative;
`;
const Padding = styled.div`
  height: 40px;
`;
const Container = styled(motion.div)`
  position: relative;
  max-width: min(400px, calc(100% - 24px));
  margin: 0 auto;
  user-select: none;
  box-sizing: border-box;
`;

const Wrapper = styled(motion.div)`
  width: 100%;
  position: relative;
  background: linear-gradient(
    to right top,
    #280537,
    #56034c,
    #890058,
    #bc005b,
    #eb1254
  );
  aspect-ratio: 85.6 / 53.98;
  border-radius: 18px;
  position: relative;
  box-shadow: rgba(0, 0, 0, 0.4) 0 2px 4px;
  box-sizing: border-box;
  padding: 4px;
  color: #fff;
  overflow: hidden;
`;

const CardContent = styled.div`
  position: relative;
  z-index: 100;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const CardInside = styled.div`
  font-family: Montserrat;
  padding: 24px 32px;
  padding-top: 0;
  padding-bottom: 42px;
  text-shadow: rgba(0, 0, 0, 0.6) 1px 1px 2px;

  .width_360 & {
    padding: 12px 16px;
    padding-bottom: 36px;
  }
`;

const CardInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-items: flex-end;
  font-family: Montserrat;
  font-size: 24px;
  font-variant-numeric: tabular-nums;
`;
const CardNumber = styled.div``;
const ExpirationDate = styled.div`
  font-size: 16px;
`;
const Nickname = styled.div`
  margin-bottom: 8px;
`;
const UpdateNotice = styled(Notice)`
  position: absolute;
  top: 20px;
  left: -6px;
  z-index: 10;
  padding: 6px 12px;
`;
